import {Apartment} from './apartment.model';
import {environment} from '../../../environments/environment';

export interface TextContent {
  es?: string;
  en?: string;
  fr?: string;
}

export interface Home {
  has_pois: boolean,
  has_how_works: boolean,
  has_house_rules: boolean,
  has_welcome: boolean,
  has_recommendations: boolean
}

export interface ReservationApartment {
  id: number;
  name: string;
  street: string;
  number: string;
  stairs: string | null;
  floor: string;
  door: string;
  city: string;
  country: string;
  postcode: string;
  notes: string | null;
  name_environment: string | null;
  id_environment: string | null;
  id_apartment_pms: string | null;
  long: string;
  lat: string;
  owner: string;
  portal_message: string | null;
  doorbell_message: string | null;
  portal_door_message: string;
  landing_door_message: string;
  main_door_message: string;
  auxiliar_door2_message: string;
  auxiliar_door3_message: string;
  auxiliar_door4_message: string;
  bell: number;
  has_check_in: number;
  has_payment: number;
  has_opendoor: number;
  welcome_picture: string | null;
  welcome_video: string;
  welcome_text: string;
  url_plans_es: string;
  url_plans_en: string;
  url_plans_fr: string;
  active: number;
  intercom: number;
  tz_offset: string | null;
  open_door_system: string;
  has_landing_door: boolean;
  has_main_door: boolean;
  has_portal_door: boolean;
  has_aux_door2: boolean;
  has_aux_door3: boolean;
  has_aux_door4: boolean;
  push_portal_first: number;
  app_sections: any[];
  home: Home;
}

export interface DateTimeObject {
  date: string;
  timezone_type: number;
  timezone: string;
}

export interface ReservationData {
  guest_code: string;
  name: string;
  surname: string;
  email: string;
  checkin_datetime: string;
  checkout_datetime: string;
  adults: number;
  childrens: number;
  text: TextContent;
  apartment: ReservationApartment;
  id_booking_pms: string | null;
  id_apartment_pms: string | null;
  client_ID: string | null;
  opening_auth: number;
  check_in: number;
  pay: number;
  trust: number;
  download_app: number;
  open_door: number;
  created_at: DateTimeObject;
  updated_at: DateTimeObject;
  now: string;
  count_notifications_unread: number;
  use_deposit_payments: boolean;
  deposit_paid: boolean;
  contract_necessary: boolean;
}

export interface Reservation {
  data: ReservationData;
}

function getFallbackPlansUrl(reservation: Reservation): string {
  switch (environment.defaultVideoLang) {
    case 'fr':
      return reservation.data.apartment.url_plans_fr || '';
    case 'en':
      return reservation.data.apartment.url_plans_en || '';
    default:
      return reservation.data.apartment.url_plans_es || '';
  }
}

function getReservationPlansUrlByLanguage(reservation: Reservation, lang: string): string {
  switch (lang) {
    case 'es':
      return reservation.data.apartment.url_plans_es || getFallbackPlansUrl(reservation);
    case 'en':
      return reservation.data.apartment.url_plans_en || getFallbackPlansUrl(reservation);
    case 'fr':
      return reservation.data.apartment.url_plans_fr || getFallbackPlansUrl(reservation);
    default:
      return getFallbackPlansUrl(reservation);
  }
}

export function getReservationPlansUrl(reservation: Reservation): string {
  return getReservationPlansUrlByLanguage(reservation, environment.defaultAppLang);
}

export function requiresDeposit(reservation: Reservation): boolean {
  if (!reservation.data.use_deposit_payments) {
    return false;
  }
  return !reservation.data.deposit_paid;
}

export function requiresCheckIn(reservation: Reservation): boolean {
  return reservation.data.trust === 0;
}

export function requiresContract(reservation: Reservation): boolean {
  return reservation.data.contract_necessary;
}

export function blockedDoor(reservation: Reservation): boolean {
  return reservation.data.opening_auth === 1;
}

export function expiredReservation(reservation: Reservation): boolean {
  return reservation.data.now > reservation.data.checkout_datetime;
}

export function futureReservation(reservation: Reservation): boolean {
  return reservation.data.now < reservation.data.checkin_datetime;
}

export function availableDate(reservation: Reservation): string {
  const d = new Date(reservation.data.checkin_datetime);
  if (Number.isNaN(d.getTime())) return '';

  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');

  return `${dd}-${mm}-${yyyy} a las ${hh}:${min} (Hora local)`;

}
export function getAppSectionName(reservation: Reservation, position: number): string {
  return getAppSectionNameByLanguage(reservation, position, environment.defaultAppLang);
}

export function getAppSectionUrl(reservation: Reservation, position: number): string {
  return getAppSectionUrlByLanguage(reservation, position, environment.defaultAppLang);
}

function getAppSectionNameByLanguage(reservation: Reservation, position: number, lang: string): string {
  switch (lang) {
    case 'es':
      return reservation.data.apartment.app_sections[position].name_es || '';
    case 'en':
      return reservation.data.apartment.app_sections[position].name_en || '';
    case 'fr':
      return reservation.data.apartment.app_sections[position].name_fr || '';
    default:
      return reservation.data.apartment.app_sections[position].name_es || '';
  }
}

function getAppSectionUrlByLanguage(reservation: Reservation, position: number, lang: string): string {
  switch (lang) {
    case 'es':
      return reservation.data.apartment.app_sections[position].url_es || '';
    case 'en':
      return reservation.data.apartment.app_sections[position].url_en || '';
    case 'fr':
      return reservation.data.apartment.app_sections[position].url_fr || '';
    default:
      return reservation.data.apartment.app_sections[position].url_es || '';
  }
}
